<template>
	<el-dialog
		:visible.sync="innerVisible"
		:title="titleDesc"
		custom-class="c-custom-dialog c-custom-dialog-with-fields"
		:before-close="handleClose"
		:close-on-click-modal="false"
		destroy-on-close
	>
		<DialogMaskImage />

		<FieldsFormPanel ref="panelRef" :personal-fields="personalFields" @update-area-code="handleAreaCodeUpdate">
			<template #tip>
				<p class="tip-content">{{ leaveWordTipDesc }}</p>
			</template>
		</FieldsFormPanel>

		<div v-if="personalFields.length" slot="footer" class="dialog-footer">
			<el-button @click="handleClose">{{ $t('enterprise.cancel') }}</el-button>
			<el-button :loading="loading" type="primary" @click="submitForm">{{ $t('enterprise.submit') }}</el-button>
		</div>
	</el-dialog>
</template>

<script setup>
import { ref, computed, getCurrentInstance, nextTick } from 'vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { Message } from 'irm-ui';
import FieldsFormPanel from '@rebuild/components/custom-fields/FieldsFormPanel.vue';
import { usePersonalFields } from '@rebuild/components/custom-fields/usePersonalFields';
import { SYSTEM_FIELD_MAP } from '@rebuild/components/custom-fields/systemFieldMap';
import { useFieldDesc } from '@rebuild/components/custom-fields/useFieldDesc';
import DialogMaskImage from '@rebuild/components/DialogMaskImage.vue';

const { proxy } = getCurrentInstance();

const { leave_word_create, leave_word_config } = useModuleData();

const props = defineProps({
	visible: {
		type: Boolean,
		default: false
	}
});
const emits = defineEmits(['update:visible', 'submit']);

const innerVisible = computed({
	get: () => {
		if (props.visible) initLeaveWordConfig();
		return props.visible;
	},
	set: val => {
		emits('update:visible', val);
	}
});

const handleClose = () => {
	personalFields.value.forEach(item => {
		proxy.$set(item, 'dynamicFieldValue', '');
	});
	areaCode.value = '+86';
	innerVisible.value = false;
	nextTick(() => {
		panelRef.value && panelRef.value.clearValidate();
		panelRef.value && panelRef.value.resetFields();
	});
};

const panelRef = ref(null);
const areaCode = ref('+86');
const handleAreaCodeUpdate = value => {
	areaCode.value = value || '+86';
};

const submitForm = async () => {
	const valid = panelRef.value ? await panelRef.value.validate() : true;
	if (!valid) return;

	const systemData = {};
	personalFields.value.forEach(item => {
		if (item.fieldType !== 0) return;
		const key = SYSTEM_FIELD_MAP[String(item.systemType)];
		if (!key) return;
		systemData[key] = item.dynamicFieldValue;
	});

	const params = {
		...systemData,
		areaCode: areaCode.value
	};

	const processedCustomFields = processCustomFields();
	if (processedCustomFields.length > 0) {
		params.newAddDataFiled = processedCustomFields;
	}
	const cacheConfig = getCacheDynamicFieldConfig();
	if (cacheConfig) {
		params.dynamicFieldConfigVO = cacheConfig;
	}

	handleFeedbackSubmit(params);
};

const loading = ref(false);
const handleFeedbackSubmit = async data => {
	loading.value = true;
	await leave_word_create.refresh({ ...data });
	panelRef.value && panelRef.value.resetFields();
	Message.success(proxy.$t('enterprise.message-sent-success'));
	innerVisible.value = false;
	loading.value = false;
};

const config = ref({});
const {
	personalFields,
	initDynamicField,
	processCustomFields,
	saveCacheDynamicFieldConfig,
	getCacheDynamicFieldConfig
} = usePersonalFields();

const titleListRef = computed(() => config.value?.titleList || []);
const leaveWordTipListRef = computed(() => config.value?.leaveWordTipList || []);
const { firstDesc: titleDesc } = useFieldDesc(titleListRef);
const { firstDesc: leaveWordTipDesc } = useFieldDesc(leaveWordTipListRef);

const initLeaveWordConfig = async () => {
	await leave_word_config.refresh();
	config.value = leave_word_config.data.data || {};
	const { languageDynamicFieldConfigVO, dynamicFieldConfigVO } = config.value;

	initDynamicField(languageDynamicFieldConfigVO);
	saveCacheDynamicFieldConfig(dynamicFieldConfigVO);
};
</script>
<style lang="less" scoped>
/deep/.c-custom-dialog {
	width: auto;
	min-width: 360px;
	max-width: 600px;
	background: linear-gradient(180deg, #e3eaff 0%, #fff 19.44%);

	.el-dialog__header {
		padding: 32px;
		border-bottom: none;

		.el-dialog__title {
			color: #1d2129;

			font-size: 20px;

			font-weight: 500;
			line-height: 28px; /* 140% */
		}
	}
	.el-dialog__headerbtn {
		top: 30px;
		right: 28px;
	}
	.el-dialog__body {
		padding-top: 16px;
		.el-form-item__content {
			width: 350px;
		}
	}
}
/deep/ .el-dialog .el-form .el-form-item__label {
	width: 100px !important;
}
/deep/ .el-dialog .el-form .el-form-item__content {
	margin-left: 110px !important;
}
/deep/ .el-form .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::after {
	line-height: 28px;
}
.tip-content {
	padding-bottom: 48px;
	color: #4e5969;
	font-size: 14px;
	font-weight: 400;
	line-height: 18px; /* 128.571% */
	width: 460px;
}
</style>
